import React, { useState, useEffect } from "react";

// Invoice line item 의 AI HS 분류 근거 modal — Google AI Studio 톤 (모노 + accent blue).
// keyword (description + composition) 로 /api/onto/action/EvidenceClassify 5-layer 호출
// → 사용자가 결정 / 다른 HSK / 취소 중 선택
//
// Props:
//   open, onClose, rowIndex, description, composition
//   onConfirm(rowIndex, hsk, detail)

const C = {
  text: "#1f1f1f",
  textMuted: "#5f6368",
  textFaint: "#80868b",
  border: "#dadce0",
  borderSoft: "#e8eaed",
  bg: "#ffffff",
  bgSoft: "#f8f9fa",
  accent: "#1a73e8",
  accentBg: "#e8f0fe",
};

function ConfidenceChip({ level }) {
  if (!level) return null;
  const label =
    level === "high" ? "HIGH" : level === "medium" ? "MEDIUM" : "LOW";
  const intensity = level === "high" ? 1 : level === "medium" ? 0.5 : 0.22;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "2px 8px",
        borderRadius: 10,
        fontSize: 11,
        fontWeight: 500,
        color: level === "high" ? "#fff" : C.text,
        background:
          level === "high"
            ? C.accent
            : level === "medium"
              ? C.accentBg
              : C.bgSoft,
        border: level === "low" ? `1px solid ${C.border}` : "none",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: level === "high" ? "#fff" : C.accent,
          opacity: intensity,
        }}
      />
      {label}
    </span>
  );
}

function SectionBox({ title, children }) {
  return (
    <div
      style={{
        padding: 12,
        border: `1px solid ${C.borderSoft}`,
        borderRadius: 8,
        fontSize: 12,
        background: C.bg,
      }}
    >
      <div
        style={{
          color: C.textMuted,
          fontSize: 11,
          marginBottom: 6,
          fontWeight: 500,
          letterSpacing: 0.2,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

export default function InvoiceLineEvidence({
  open,
  onClose,
  rowIndex,
  description,
  composition,
  onConfirm,
}) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [manualHsk, setManualHsk] = useState("");

  const keyword = composition
    ? `${(description || "").trim()} ${composition.trim()}`.trim()
    : (description || "").trim();

  useEffect(() => {
    if (!open || !keyword) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetail(null);
    setManualHsk("");
    fetch("/api/onto/action/EvidenceClassify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ keyword }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        if (d?.error) setError(d.error);
        else setDetail(d);
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, keyword]);

  if (!open) return null;

  const decided = detail?.decided;
  const layers = detail?.layers || {};
  const precEv = (detail?.evidence || []).find(
    (e) => e.source === "precedent_cases",
  );

  const handleConfirm = (hskOverride) => {
    const hsk = hskOverride || decided?.hsk;
    if (!hsk || !/^\d{10}$/.test(hsk)) return;
    onConfirm?.(rowIndex, hsk, detail);
    onClose?.();
  };

  const cleanedManual = manualHsk.replace(/[^0-9]/g, "");
  const manualValid = cleanedManual.length === 10;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(32,33,36,0.5)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.bg,
          borderRadius: 8,
          padding: 20,
          width: 740,
          maxWidth: "92vw",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow:
            "0 1px 3px rgba(60,64,67,0.15), 0 4px 12px rgba(60,64,67,0.12)",
          display: "flex",
          flexDirection: "column",
          gap: 12,
          color: C.text,
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 500, color: C.text }}>
              AI HS 분류 근거
            </div>
            <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
              {keyword || "(키워드 없음)"}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              fontSize: 18,
              cursor: "pointer",
              color: C.textMuted,
              padding: 2,
              lineHeight: 1,
            }}
            aria-label="close"
          >
            ✕
          </button>
        </div>

        {loading && (
          <div style={{ color: C.textMuted, fontSize: 12, padding: "16px 0" }}>
            5-layer 분석 중…
          </div>
        )}
        {error && (
          <div
            style={{
              color: C.textMuted,
              fontSize: 12,
              padding: 10,
              background: C.bgSoft,
              borderRadius: 6,
              border: `1px solid ${C.borderSoft}`,
            }}
          >
            오류: {error}
          </div>
        )}

        {detail && (
          <>
            {/* 결정 */}
            {decided ? (
              <div
                style={{
                  padding: 14,
                  background: C.accentBg,
                  borderRadius: 8,
                  border: `1px solid ${C.border}`,
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    color: C.textMuted,
                    fontWeight: 500,
                  }}
                >
                  결정
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginTop: 4,
                    flexWrap: "wrap",
                  }}
                >
                  <code
                    style={{
                      fontSize: 18,
                      fontWeight: 600,
                      color: C.text,
                      letterSpacing: 0.3,
                    }}
                  >
                    {decided.hsk}
                  </code>
                  <ConfidenceChip level={decided.confidence} />
                  {typeof decided.score === "number" && (
                    <span style={{ fontSize: 11, color: C.textMuted }}>
                      score {decided.score.toFixed(2)}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: C.text, marginTop: 6 }}>
                  {decided.popum_ko || "(품명불명)"}
                  {decided.popum_en && (
                    <span style={{ color: C.textMuted }}>
                      {" · "}
                      {decided.popum_en}
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div
                style={{
                  padding: 12,
                  background: C.bgSoft,
                  borderRadius: 8,
                  border: `1px solid ${C.border}`,
                  color: C.textMuted,
                  fontSize: 12,
                }}
              >
                결정 불가 (LOW confidence) — 수동 분류 또는 다른 키워드로 재시도
                필요
              </div>
            )}

            {/* 1. 통관 사례 */}
            <SectionBox title="통관 사례 (forward)">
              {precEv ? (
                <>
                  <div style={{ color: C.text }}>
                    {precEv.n_total}건 매칭 → 다수결{" "}
                    <code style={{ color: C.accent }}>
                      {precEv.majority_hsk}
                    </code>{" "}
                    <span style={{ color: C.textMuted }}>
                      ({Math.round(precEv.majority_ratio * 100)}%,{" "}
                      {precEv.top_hsk_n}건)
                    </span>
                  </div>
                  {precEv.snippets?.length > 0 && (
                    <div
                      style={{
                        marginTop: 8,
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                      }}
                    >
                      {precEv.snippets.slice(0, 3).map((s, i) => (
                        <div
                          key={i}
                          style={{
                            fontSize: 11,
                            color: C.textMuted,
                            paddingLeft: 10,
                            borderLeft: `2px solid ${C.borderSoft}`,
                            lineHeight: 1.5,
                          }}
                        >
                          <code style={{ color: C.accent }}>{s.hsk}</code>
                          {" · "}
                          <span style={{ color: C.text }}>{s.label}</span>
                          {s.snippet && (
                            <div
                              style={{
                                color: C.textFaint,
                                marginTop: 2,
                                fontSize: 10.5,
                              }}
                            >
                              {s.snippet}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ color: C.textFaint }}>매칭 사례 없음</div>
              )}
            </SectionBox>

            {/* 2. 관세율표 본문 역검증 */}
            <SectionBox title="관세율표 본문 역검증 (backward)">
              {layers.layer4_reverse?.available ? (
                <div style={{ color: C.text }}>
                  키워드 토큰{" "}
                  <span style={{ fontWeight: 500 }}>
                    {layers.layer4_reverse.matched_n}/
                    {layers.layer4_reverse.total_tokens}
                  </span>{" "}
                  일치 ({Math.round(layers.layer4_reverse.match_ratio * 100)}%)
                  <span
                    style={{
                      marginLeft: 6,
                      fontSize: 11,
                      color: layers.layer4_reverse.pass
                        ? C.accent
                        : C.textMuted,
                    }}
                  >
                    {layers.layer4_reverse.pass ? "일치" : "불일치"}
                  </span>
                  <div
                    style={{
                      fontSize: 11,
                      color: C.textMuted,
                      marginTop: 4,
                      lineHeight: 1.5,
                    }}
                  >
                    {layers.layer4_reverse.name_ko}
                    {layers.layer4_reverse.popum && (
                      <span style={{ color: C.textFaint }}>
                        {" · "}
                        {layers.layer4_reverse.popum}
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ color: C.textFaint }}>역검증 불가</div>
              )}
            </SectionBox>

            {/* 3. AI 의미 vector 분석 */}
            <SectionBox title="AI 의미 vector 분석">
              {layers.layer3_embedding?.available ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {(layers.layer3_embedding.top3 || []).map((t, i) => (
                    <span
                      key={i}
                      style={{
                        fontSize: 11,
                        padding: "3px 8px",
                        background: C.bgSoft,
                        border: `1px solid ${C.borderSoft}`,
                        borderRadius: 4,
                        color: C.text,
                      }}
                    >
                      <code style={{ color: C.accent }}>{t.hsk}</code>{" "}
                      <span style={{ color: C.textMuted }}>{t.n}건</span>
                    </span>
                  ))}
                </div>
              ) : (
                <div style={{ color: C.textFaint }}>embedding 비활성</div>
              )}
            </SectionBox>

            {/* 4. Gemini 검토 */}
            <SectionBox title="AI 검토 (Gemini)">
              {layers.layer2_gemini?.available ? (
                <div style={{ color: C.text }}>
                  <span
                    style={{
                      fontWeight: 500,
                      color: layers.layer2_gemini.agree
                        ? C.accent
                        : C.textMuted,
                    }}
                  >
                    {layers.layer2_gemini.agree ? "동의" : "이견"}
                  </span>
                  {layers.layer2_gemini.suggested_hsk &&
                    layers.layer2_gemini.suggested_hsk !== decided?.hsk && (
                      <span style={{ color: C.textMuted }}>
                        {" — 제안: "}
                        <code style={{ color: C.accent }}>
                          {layers.layer2_gemini.suggested_hsk}
                        </code>
                      </span>
                    )}
                  {layers.layer2_gemini.reason && (
                    <div
                      style={{
                        fontSize: 11,
                        color: C.textMuted,
                        marginTop: 4,
                        lineHeight: 1.55,
                      }}
                    >
                      {layers.layer2_gemini.reason}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ color: C.textFaint }}>Gemini 호출 안 됨</div>
              )}
            </SectionBox>

            {/* 액션 */}
            <div
              style={{
                display: "flex",
                gap: 8,
                marginTop: 6,
                paddingTop: 12,
                borderTop: `1px solid ${C.borderSoft}`,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              {decided?.hsk && (
                <button
                  onClick={() => handleConfirm()}
                  style={{
                    padding: "7px 16px",
                    background: C.accent,
                    color: "#fff",
                    border: "none",
                    borderRadius: 4,
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: 500,
                    letterSpacing: 0.2,
                  }}
                >
                  {decided.hsk} 확정
                </button>
              )}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginLeft: "auto",
                }}
              >
                <input
                  type="text"
                  placeholder="다른 HSK (10자리)"
                  value={manualHsk}
                  onChange={(e) => setManualHsk(e.target.value.slice(0, 12))}
                  style={{
                    padding: "6px 10px",
                    border: `1px solid ${C.border}`,
                    borderRadius: 4,
                    fontSize: 12,
                    width: 180,
                    fontFamily: "monospace",
                    color: C.text,
                    outline: "none",
                  }}
                />
                <button
                  onClick={() => {
                    if (manualValid) handleConfirm(cleanedManual);
                    else alert("HSK 10자리 숫자가 필요합니다.");
                  }}
                  disabled={!manualValid}
                  style={{
                    padding: "6px 12px",
                    background: manualValid ? C.text : C.bgSoft,
                    color: manualValid ? "#fff" : C.textFaint,
                    border: manualValid ? "none" : `1px solid ${C.border}`,
                    borderRadius: 4,
                    cursor: manualValid ? "pointer" : "not-allowed",
                    fontSize: 12,
                  }}
                >
                  직접 확정
                </button>
              </div>
              <button
                onClick={onClose}
                style={{
                  padding: "6px 14px",
                  background: C.bg,
                  color: C.textMuted,
                  border: `1px solid ${C.border}`,
                  borderRadius: 4,
                  cursor: "pointer",
                  fontSize: 12,
                }}
              >
                취소
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
